import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Button,
  Avatar,
  Box,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Slider,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import {
  PhotoCamera as IconPhotoCamera,
  Image as ImageIcon,
  Close as IconClose,
  CropSquare as IconSquare,
  CropLandscape as IconLandscape,
  CropPortrait as IconPortrait,
} from "@mui/icons-material";
import Cropper from "react-easy-crop";
import getCroppedImg from "./cropImage";
import { v4 as uuid_v4 } from "../../libs/uuid";

const getFileExt = (name = "") => {
  const i = String(name).lastIndexOf(".");
  return i >= 0 ? String(name).slice(i + 1).toLowerCase() : "";
};

const mimeTypeToExt = (mimeType = "") => {
  const normalized = String(mimeType).toLowerCase();
  if (normalized === "image/png") return "png";
  if (normalized === "image/jpeg") return "jpg";
  if (normalized === "image/webp") return "webp";
  return "png";
};

const PrettoSlider = styled(Slider)({
  color: "#52af77",
  height: 8,
  "& .MuiSlider-track": { border: "none" },
  "& .MuiSlider-thumb": {
    height: 24,
    width: 24,
    backgroundColor: "#fff",
    border: "2px solid currentColor",
    "&:focus, &:hover, &.Mui-active, &.Mui-focusVisible": { boxShadow: "inherit" },
    "&:before": { display: "none" },
  },
});

const ThumbnailUploadWithEditor = ({
  url,
  onChange,
  onDelete = () => {},
  variant = "square",
  aspect = 1,
  width = 240,
  height = 120,
  Icon = ImageIcon,
  isAvatar = true,
  showAspectRatioButtons = false,
}) => {
  const fileRef = useRef();
  const [avatarSrc, setAvatarSrc] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [uploadFileName, setUploadFileName] = useState("");
  const [selectedAspect, setSelectedAspect] = useState(aspect);
  const [tempImageSrc, setTempImageSrc] = useState(null);
  const initialCrop = { x: 0, y: 0 };
  const [crop, setCrop] = useState({ ...initialCrop });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

  useEffect(() => { setAvatarSrc(url); }, [url]);

  useEffect(() => {
    if (tempImageSrc) {
      setCrop({ ...initialCrop });
      setRotation(0);
      setCroppedAreaPixels(null);
      setZoom(1);
      setSelectedAspect(aspect);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tempImageSrc]);

  const handleOnChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const imageUrl = URL.createObjectURL(e.target.files[0]);
      setTempImageSrc(imageUrl);
      const sourceExt = getFileExt(e.target.files[0].name);
      const ext = sourceExt || "png";
      setUploadFileName(`${uuid_v4()}.${ext}`);
      fileRef.current.value = "";
      setOpenDialog(true);
    }
  };

  const onCropComplete = useCallback((croppedArea, areaPixels) => {
    setCroppedAreaPixels(areaPixels);
  }, []);

  const showCroppedImage = useCallback(async () => {
    try {
      const croppedImage = await getCroppedImg(tempImageSrc, croppedAreaPixels, rotation);
      const croppedUrl = URL.createObjectURL(croppedImage);
      const blobType = croppedImage?.type || "image/png";
      const safeFileName = uploadFileName?.includes(".")
        ? uploadFileName
        : `${uuid_v4()}.${mimeTypeToExt(blobType)}`;
      const file = new File([croppedImage], safeFileName, { type: blobType });
      setAvatarSrc(croppedUrl);
      setTempImageSrc(null);
      onChange(file);
    } catch (err) {
      console.error(err);
    }
  }, [tempImageSrc, croppedAreaPixels, rotation, uploadFileName, onChange]);

  const onWheel = (e) => {
    e.preventDefault();
    const newZoom = zoom + e.deltaY * 0.01;
    setZoom(Math.max(0.25, Math.min(4, newZoom)));
  };

  const handleAspectChange = (event, newAspect) => {
    if (newAspect !== null) {
      setSelectedAspect(newAspect);
      setCrop({ ...initialCrop });
    }
  };

  return (
    <Box sx={{ textAlign: "center", width: "100%", position: "relative" }} display="flex" alignItems="flex-end" justifyContent="center">
      <Dialog
        open={openDialog}
        onClose={() => { setTempImageSrc(null); setOpenDialog(false); }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pr: 1 }}>
          Edit image
          <IconButton size="small" onClick={() => { setTempImageSrc(null); setOpenDialog(false); }}>
            <IconClose fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ height: 300, position: "relative", mb: 2 }} onWheel={onWheel}>
            <Cropper
              key={`cropper-${selectedAspect}`}
              image={tempImageSrc}
              rotation={rotation}
              crop={crop}
              zoom={zoom}
              restrictPosition={false}
              aspect={selectedAspect}
              onCropChange={setCrop}
              onCropComplete={onCropComplete}
              onZoomChange={() => {}}
              zoomWithScroll={true}
              cropShape={variant === "circle" ? "round" : "rect"}
            />
          </Box>
          {showAspectRatioButtons && (
            <Box display="flex" justifyContent="center" sx={{ mb: 2 }}>
              <ToggleButtonGroup value={selectedAspect} exclusive onChange={handleAspectChange} aria-label="aspect ratio" size="small">
                <ToggleButton value={1} aria-label="1:1 square"><IconSquare sx={{ mr: 0.5 }} fontSize="small" />1:1</ToggleButton>
                <ToggleButton value={16 / 9} aria-label="16:9 landscape"><IconLandscape sx={{ mr: 0.5 }} fontSize="small" />16:9</ToggleButton>
                <ToggleButton value={9 / 16} aria-label="9:16 portrait"><IconPortrait sx={{ mr: 0.5 }} fontSize="small" />9:16</ToggleButton>
              </ToggleButtonGroup>
            </Box>
          )}
          <Box display="flex" alignItems="center" sx={{ px: 1 }}>
            <Typography variant="body2" sx={{ width: 80 }}>Zoom</Typography>
            <PrettoSlider size="small" valueLabelDisplay="auto" min={0.5} max={3} step={0.1} value={zoom} onChange={(e) => setZoom(e.target.value)} />
          </Box>
          <Box display="flex" alignItems="center" mt={2} sx={{ px: 1 }}>
            <Typography variant="body2" sx={{ width: 80 }}>Rotation</Typography>
            <PrettoSlider size="small" valueLabelDisplay="auto" min={0} max={360} step={1} value={rotation} onChange={(e) => setRotation(e.target.value)} />
          </Box>
        </DialogContent>
        <DialogActions sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end" }}>
          <Button onClick={() => { setTempImageSrc(null); setOpenDialog(false); }} sx={{ textTransform: "none" }}>Cancel</Button>
          <Button
            variant="contained"
            onClick={async () => { await showCroppedImage(); setOpenDialog(false); }}
            sx={{ textTransform: "none", backgroundColor: "#16a34a", "&:hover": { backgroundColor: "#15803d" } }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {isAvatar ? (
        <Box sx={{ position: "relative", width: "100%", display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
          <Box
            sx={{
              position: "relative",
              borderRadius: variant === "circle" ? "50%" : "8px",
              overflow: "hidden",
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              border: "2px solid #e0e0e0",
              transition: "all 0.2s ease",
              "&:hover": { boxShadow: "0 4px 12px rgba(0,0,0,0.15)", borderColor: "#bdbdbd" },
            }}
          >
            <Avatar
              alt="Avatar"
              variant={variant === "circle" ? "circular" : "square"}
              src={avatarSrc}
              imgProps={{ style: { maxHeight: "100%", maxWidth: "100%", objectFit: "cover" } }}
              sx={{ width, height, bgcolor: "#fafafa" }}
            >
              <Icon sx={{ fontSize: 60, color: "#bdbdbd" }} />
            </Avatar>
            {avatarSrc && (
              <IconButton
                size="small"
                onClick={() => { setAvatarSrc(null); onDelete(); }}
                sx={{
                  position: "absolute",
                  top: 8,
                  right: 8,
                  bgcolor: "rgba(255, 255, 255, 0.9)",
                  boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                  "&:hover": { bgcolor: "#fff", color: "error.main" },
                }}
              >
                <IconClose fontSize="small" />
              </IconButton>
            )}
          </Box>
          <Button
            variant="outlined"
            startIcon={<IconPhotoCamera />}
            onClick={() => fileRef.current.click()}
            sx={{
              borderRadius: "8px",
              textTransform: "none",
              px: 3,
              py: 1,
              borderColor: "#e0e0e0",
              color: "#616161",
              "&:hover": { borderColor: "#9e9e9e", bgcolor: "#f5f5f5" },
            }}
          >
            {avatarSrc ? "Change Image" : "Upload Image"}
          </Button>
          <input hidden accept="image/*" type="file" onChange={handleOnChange} ref={fileRef} />
        </Box>
      ) : (
        <Box sx={{ position: "relative", mb: 1, width: "100%" }}>
          <Box display="flex" justifyContent="center" alignItems="center" width={width} height={height} border="dashed black 1px">
            Add a new image
          </Box>
          <Box sx={{ width: "100%", height: "100%", bottom: 0, position: "absolute" }} display="flex" justifyContent="center" alignItems="center">
            <Button sx={{ width: "100%", height: "100%" }} onClick={() => fileRef.current.click()} />
            <input hidden accept="image/*" type="file" onChange={handleOnChange} ref={fileRef} />
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default ThumbnailUploadWithEditor;
